import { axiosInstance } from "../utils/axiosinstance";

export interface AdminSubmissionFilters {
  hackathonId?: string;
  page?: number;
  limit?: number;
}

export interface WeeklySubmissionFilters {
  hackathonId?: string;
  weekNumber?: string;
  status?: string;
  participantId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// Get submission by team ID
export const getSubmissionByTeamId = async (teamId: string, hackathonId?: string) => {
  try {
    const params = hackathonId ? `?hackathonId=${hackathonId}` : "";
    const response = await axiosInstance.get(
      `/api/v1/submissions/${teamId}${params}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching submission by team ID:", error);
    throw error;
  }
};

// Get all submissions (admin only, excludes drafts)
export const getAllSubmissions = async (filters: AdminSubmissionFilters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.hackathonId) params.append("hackathonId", filters.hackathonId);
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());

    const response = await axiosInstance.get(
      `/api/v1/submissions/admin/submissions?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching all submissions:", error);
    throw error;
  }
};

// Get submission by ID (admin only)
export const getSubmissionById = async (id: string) => {
  try {
    const response = await axiosInstance.get(
      `/api/v1/submissions/admin/submissions/${id}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching submission by ID:", error);
    throw error;
  }
};

// Export submissions as CSV/Excel (admin only)
export const exportSubmissions = async (
  hackathonId?: string,
  format: "csv" | "excel" = "excel"
) => {
  try {
    const params = new URLSearchParams();
    if (hackathonId) params.append("hackathonId", hackathonId);
    params.append("format", format);

    const response = await axiosInstance.get(
      `/api/v1/submissions/admin/submission/export?${params.toString()}`,
      {
        responseType: "blob",
      }
    );

    // Create a download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `submissions.${format === "csv" ? "csv" : "xlsx"}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error("Error exporting submissions:", error);
    throw error;
  }
};

// Fetch all weekly submissions with filters
export const fetchWeeklySubmissions = async (
  filters: WeeklySubmissionFilters = {}
) => {
  try {
    const params = new URLSearchParams();

    if (filters.hackathonId) params.append("hackathonId", filters.hackathonId);
    if (filters.weekNumber) params.append("weekNumber", filters.weekNumber);
    if (filters.status) params.append("status", filters.status);
    if (filters.participantId)
      params.append("participantId", filters.participantId);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());

    const response = await axiosInstance.get(
      `/api/v1/weekly-submissions?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching weekly submissions:", error);
    throw error;
  }
};

// Fetch single weekly submission by ID
export const fetchWeeklySubmissionById = async (id: string) => {
  try {
    const response = await axiosInstance.get(
      `/api/v1/weekly-submissions/${id}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching weekly submission:", error);
    throw error;
  }
};

// Fetch weekly submission statistics
export const fetchWeeklySubmissionStats = async (
  hackathonId?: string,
  weekNumber?: string
) => {
  try {
    const params = new URLSearchParams();
    if (hackathonId) params.append("hackathonId", hackathonId);
    if (weekNumber) params.append("weekNumber", weekNumber);

    const response = await axiosInstance.get(
      `/api/v1/weekly-submissions/stats?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching weekly submission stats:", error);
    throw error;
  }
};

// Export weekly submissions as CSV
export const exportWeeklySubmissionsAsCSV = async (
  filters: WeeklySubmissionFilters = {}
) => {
  try {
    const params = new URLSearchParams();

    if (filters.hackathonId) params.append("hackathonId", filters.hackathonId);
    if (filters.weekNumber) params.append("weekNumber", filters.weekNumber);
    if (filters.status) params.append("status", filters.status);

    const response = await axiosInstance.get(
      `/api/v1/weekly-submissions/export/csv?${params.toString()}`,
      {
        responseType: "blob",
      }
    );

    // Create a download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "weekly-submissions.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error("Error exporting weekly submissions as CSV:", error);
    throw error;
  }
};

// Export weekly submissions as Excel
export const exportWeeklySubmissionsAsExcel = async (
  filters: WeeklySubmissionFilters = {}
) => {
  try {
    const params = new URLSearchParams();

    if (filters.hackathonId) params.append("hackathonId", filters.hackathonId);
    if (filters.weekNumber) params.append("weekNumber", filters.weekNumber);
    if (filters.status) params.append("status", filters.status);

    const response = await axiosInstance.get(
      `/api/v1/weekly-submissions/export/excel?${params.toString()}`,
      {
        responseType: "blob",
      }
    );

    // Create a download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "weekly-submissions.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error("Error exporting weekly submissions as Excel:", error);
    throw error;
  }
};
